import React, { useState, useEffect } from 'react';
import {
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import {
  Input, Checkbox, Table, TablePaginationConfig, Button,
  Divider, notification, Menu, Dropdown, Radio, Row, Col,
  Modal, Form, FormInstance, Badge,
} from 'antd';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import './ListUsers.scss';
import {
  patch, RequestWrapper, ResponseWrapper,
  put, get, post,
  isSuperAgentError,
} from '../../dataSource/requests';

const ListUsers = (): React.ReactElement => {
  interface UserRecord {
    creationDate: string,
    email: string,
    id: number,
    name: string,
    roleName: string,
    type: string,
    userStatus: string,
  }

  interface TablePagination {
    current: number | undefined,
    defaultCurrent: number | undefined,
    pageSize: number | undefined,
    showQuickJumper: boolean | {
      goButton?: React.ReactNode;
    },
    total: number | undefined,
    itemsInPage: number | undefined,
  }

  interface PermissionRecord {
    category: string;
    feature: string;
    type: string;
  }

  interface RoleRecord {
    defaultRole?: boolean;
    id: number | undefined;
    name: string;
    permissions?: Array<PermissionRecord>;
    status?: string;
    type: string;
  }

  interface FilterFormFields {
    nome: string | undefined;
    tipo: string | undefined;
    perfil: string | number | undefined;
    status: string | undefined;
    aprovacaoPendente: boolean | undefined;
  }

  interface RelevantPaginationInfo {
    loading: boolean;
  }

  const [formInstance] = Form.useForm();
  const navigate = useNavigate();

  const [reload, setReload] = useState<number>(1);
  const [roles, setRoles] = useState<Array<RoleRecord>>([]);
  const [data, setData] = useState<Array<UserRecord>>([]);
  const [loadingExcel, setLoadingExcel] = useState<boolean>(false);
  const [tableDefinition, setTableDefinition] = useState<RelevantPaginationInfo>({
    loading: false,
  });

  const [tablePagination, setTablePagination] = useState<TablePagination>({
    pageSize: 15,
    current: 1,
    defaultCurrent: 1,
    showQuickJumper: true,
    total: 0,
    itemsInPage: undefined,
  });

  const [statuss] = useState([
    {
      code: 'ACTIVATED',
      description: 'Ativo',
      types: ['COOPERATIVE', 'COOPERCITRUS'],
    },
    {
      code: 'DEACTIVATED',
      description: 'Inativo',
      types: ['COOPERATIVE', 'COOPERCITRUS'],
    },
    {
      code: 'EXCLUSION_REQUESTED',
      description: 'Aguardando exclusão',
      types: ['COOPERATIVE', 'COOPERCITRUS'],
    },
    {
      code: 'EXCLUSION_APPROVED',
      description: 'Excluído via solicitação do usuário',
      types: ['COOPERATIVE', 'COOPERCITRUS'],
    },
    {
      code: 'AWAITING_EMAIL_CONFIRMATION',
      description: 'Email não confirmado',
      types: ['COOPERATIVE'],
    },
    {
      code: 'ALL',
      description: 'Todos',
      types: ['COOPERATIVE', 'COOPERCITRUS'],
    },
  ]);

  const [tipos] = useState([
    { name: 'Cooperado', value: 'COOPERATIVE' },
    { name: 'Coopercitrus', value: 'COOPERCITRUS' },
  ]);

  const executeFilter = async (parameters: {
    page: number,
  } = {
    page: 0,
  }): Promise<void> => {
    setTableDefinition((previous) => {
      return ({
        ...previous,
        loading: true,
      });
    });

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const values = formInstance.getFieldsValue();

    const requestReference: RequestWrapper = patch({
      url: '/user/listAll',

      params: {
        name: values.nome || undefined,
        roleId: values.perfil || undefined,
        status: values.status || 'ALL',
        type: values.tipo || 'COOPERATIVE',
        withPendingApprovalMatriculations: values?.aprovacaoPendente || false,
      },

      query: {
        page: parameters.page,
      },

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 1009): {
        // "All users listed successfully", 1009
        const pagination = tablePagination;
        pagination.total = response?.body.paginationInfo.totalElements;
        pagination.itemsInPage = response?.body.paginationInfo.numberOfElementsInPage;

        setData(response?.body.users);
        setTablePagination((previous) => {
          return ({
            ...previous,
            ...pagination,
          });
        });
      } break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: 'Você não possui permissão para realizar esta ação.',
        });
        break;

      case (responseError?.body?.code === -1010):
        // "Invalid list all users filters", -1010
        formInstance.resetFields();

        setTablePagination((previous) => {
          return ({
            ...previous,
            current: 1,
          });
        });
        break;

      case (responseError?.body?.code === -3000):
        // "Role not found", -3000
        notification.error({
          message: 'Erro',
          description: 'Perfil não encontrado.',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao executar esta solicitação, por favor tente novamente mais tarde.',
        });
        break;
    }

    setTableDefinition((previous) => {
      return ({
        ...previous,
        loading: false,
      });
    });
  };

  const handleUserDelete = async (userID: number): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = put({
      url: `/customer/delete/${userID}`,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      console.log(error);
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 1011):
        // "User deleted successfully", 1011
        if (tablePagination?.itemsInPage === 1) {
          if (tablePagination?.current === 1) {
            executeFilter({ page: 0 });
          } else {
            executeFilter({
              page: (tablePagination?.current)
                ? tablePagination?.current - 2
                : 0,
            });
          }
        } else {
          executeFilter({
            page: (tablePagination?.current)
              ? tablePagination?.current - 1
              : 0,
          });
        }

        notification.success({
          message: 'Sucesso',
          description: 'Usuário excluído.',
        });
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: 'você não possui permissão para realizar esta ação.',
        });
        break;

      case (responseError?.body?.code === -1007):
        // "User not found", -1007
        notification.error({
          message: 'Erro',
          description: 'Usuário não encontrado.',
        });
        break;

      case (responseError?.body?.code === -1011):
        // "User cannot delete itself", -1011
        notification.error({
          message: 'Erro',
          description: 'Não é permitido excluir o próprio usuário logado.',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, não foi possível realizar sua solicitação.',
        });
        break;
    }

    setReload(reload + 1);
  };

  const handleAdminDelete = async (adminID: number): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = put({
      url: `/admin/delete/${adminID}`,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      console.log(error);
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 1011):
        // "User deleted successfully", 1011
        if (tablePagination.itemsInPage === 1) {
          if (tablePagination.current === 1) {
            executeFilter({
              page: 0,
            });
          } else {
            executeFilter({
              page: (tablePagination?.current)
                ? tablePagination?.current - 2
                : 0,
            });
          }
        } else {
          executeFilter({
            page: (tablePagination?.current)
              ? tablePagination?.current - 1
              : 0,
          });
        }

        notification.success({
          message: 'Sucesso',
          description: 'Usuário excluído.',
        });
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: 'Você não possui permissão para realizar esta ação.',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -1007):
        // "User not found", -1007
        notification.error({
          message: 'Erro',
          description: 'Usuário não encontrado.',
        });
        break;

      case (responseError?.body?.code === -1011):
        // "User cannot delete itself", -1011
        notification.error({
          message: 'Erro',
          description: 'Não é permitido excluir o próprio usuário logado.',
        });
        break;

      case (responseError?.body?.code === -3003):
        // "At least one admin should be on system", -3003
        notification.error({
          message: 'Erro',
          description: 'Não é permitido excluir o único usuário administrador existente no sistema.',
        });
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, não foi possível realizar sua solicitação.',
        });
        break;
    }
  };

  const exportExcel = async (): Promise<void> => {
    setLoadingExcel(true);
    const url = '/user/export/excel';
    const paramsFilter = formInstance.getFieldsValue();
    const params = {
      withPendingApprovalMatriculations:
        paramsFilter.aprovacaoPendente !== undefined ? paramsFilter.aprovacaoPendente : null,
      name: paramsFilter.nome !== undefined ? paramsFilter.nome : null,
      type: paramsFilter.tipo !== undefined ? paramsFilter.tipo : 'COOPERCITRUS',
      roleId: paramsFilter.perfil !== undefined ? paramsFilter.perfil : null,
      status: paramsFilter.status !== undefined ? paramsFilter.status : 'ALL',
    };

    const requestReference: RequestWrapper = post({
      url,
      params,

      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
      },

      responseType: 'blob',

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      const resp = await requestReference;

      if (resp.status === 200) {
        const filee = new Blob([resp.body], { type: 'xlsx' });

        const a = document.createElement('a');
        const downloadUrl = window.URL.createObjectURL(filee);

        const today = new Date();
        const date = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;
        a.href = downloadUrl;
        a.download = `Usuarios ${date}.xlsx`;

        document.body.append(a);

        a.click();
        setLoadingExcel(false);

        setTimeout(() => {
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        }, 0);
      }
    } catch (error: unknown) {
      console.log(error);
      setLoadingExcel(false);
    }
  };

  const showDeleteConfirm = (type: string, userID: number): void => {
    Modal.confirm({
      title: 'Tem certeza que deseja deletar este usuario?',
      okText: 'Sim, deletar',
      okType: 'danger',
      cancelText: 'Não, cancelar',

      onOk: () => {
        switch (type) {
          case 'COOPERATIVE':
            handleUserDelete(userID);
            break;

          case 'COOPERCITRUS':
            handleAdminDelete(userID);
            break;

          default:
            break;
        }
      },

      onCancel: (): void => undefined,
    });
  };

  const [columns] = useState([
    {
      textWrap: 'word-break',
      ellipsis: true,
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      textWrap: 'word-break',
      ellipsis: true,
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',

      render: (text: string) => (
        <b>{text}</b>
      ),
    },
    {
      title: 'Data do cadastro',
      dataIndex: 'creationDate',
      key: 'creationDate',

      render: (text: string) => (
        <span>{moment(text).format('DD/MM/Y')}</span>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Perfil',
      dataIndex: 'roleName',
      key: 'roleName',
    },
    {
      title: 'Status',
      dataIndex: 'userStatus',
      key: 'userStatus',

      render: (text): React.ReactNode => {
        let actualColor = '';
        let actualText = '';

        switch (true) {
          case (text === 'ACTIVATED'):
            actualColor = 'blue';
            actualText = 'Ativo';
            break;

          case (text === 'AWAITING_EMAIL_CONFIRMATION'):
            actualColor = 'orange';
            actualText = 'Email não confirmado';
            break;

          case (text === 'DEACTIVATED'):
            actualColor = 'grey';
            actualText = 'Inativo';
            break;

          case (text === 'EXCLUSION_REQUESTED'):
            actualColor = '#FFFF00';
            actualText = 'Aguardando exclusão';
            break;

          case (text === 'EXCLUSION_APPROVED'):
            actualColor = 'red';
            actualText = 'Excluído via solicitação do usuário';
            break;

          default:
            actualColor = 'grey';
            break;
        }

        return (<Badge color={actualColor} text={actualText} />);
      },
    },
    {
      title: 'Ações',
      dataIndex: 'id',
      key: 'opcoes',

      render: (_text: string, record: UserRecord) => (
        <span>
          <Button
            icon={<EditOutlined />}
            onClick={(): void => {
              switch (record.type) {
                case 'COOPERATIVE':
                  navigate(`/user/edit/${record.id}`);
                  break;

                case 'COOPERCITRUS':
                  navigate(`/admin/edit/${record.id}`);
                  break;

                default:
                  break;
              }
            }}
          />
          <Divider type="vertical" />
          {record.userStatus !== 'EXCLUSION_REQUESTED' && record.userStatus !== 'EXCLUSION_APPROVED' && (
          <Button
            icon={<DeleteOutlined />}
            onClick={(): void => showDeleteConfirm(record.type, record.id)}
          />
          )}
        </span>
      ),
    },
  ]);

  const handleTableChange = (pagination: TablePaginationConfig): void => {
    setTableDefinition((previous) => {
      return ({
        ...previous,
        loading: true,
      });
    });

    const pager = tablePagination;

    pager.current = pagination.current;

    setTablePagination((previous) => {
      return ({
        ...previous,
        ...pager,
      });
    });

    setTableDefinition((previous) => {
      return ({
        ...previous,
        loading: false,
      });
    });

    executeFilter({
      page: (tablePagination?.current)
        ? tablePagination?.current - 1
        : 0,
    });
  };

  const handleChangedValues = async (
    _changedValues: FilterFormFields,
    allValues: FilterFormFields,
  ): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const pag = tablePagination;
    pag.current = 1;

    setTablePagination((previous) => {
      return ({
        ...previous,
        ...pag,
      });
    });

    setTableDefinition((previous) => {
      return ({
        ...previous,
        loading: true,
      });
    });

    const requestReference: RequestWrapper = patch({
      url: '/user/listAll',

      params: {
        name: allValues.nome || undefined,
        roleId: allValues.perfil || undefined,
        status: allValues.status || 'ALL',
        type: allValues.tipo || 'COOPERATIVE',
        withPendingApprovalMatriculations: allValues?.aprovacaoPendente || false,
      },

      query: {
        page: 0,
      },

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 1009): {
        // "All users listed successfully", 1009
        const pagination = tablePagination;

        pagination.total = response?.body.paginationInfo.totalElements;
        pagination.current = 1;
        pagination.itemsInPage = response?.body.paginationInfo.numberOfElementsInPage;

        setData(response?.body.users);
        setTablePagination((previous) => {
          return ({
            ...previous,
            ...pagination,
          });
        });
      } break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: 'Você não possui permissão para realizar esta ação.',
        });
        break;

      case (responseError?.body?.code === -1010):
        // "Invalid list all users filters", -1010
        formInstance.resetFields();

        setTablePagination((previous) => {
          return ({
            ...previous,
            ...tablePagination,
            current: 1,
          });
        });
        break;

      case (responseError?.body?.code === -3000):
        // "Role not found", -3000
        notification.error({
          message: 'Erro',
          description: 'Perfil não encontrado.',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao executar esta solicitação, por favor tente novamente mais tarde.',
        });
        break;
    }

    setTableDefinition((previous) => {
      return ({
        ...previous,
        loading: false,
      });
    });
  };

  const radioStyle = {
    display: 'block',
    height: '30px',
    lineHeight: '30px',
  };

  useEffect(() => {
    const firstMount = async (): Promise<void> => {
      let response: ResponseWrapper | undefined;
      let responseError: ResponseWrapper | undefined;
      let genericError: Error | undefined;

      const requestReference: RequestWrapper = get({
        url: '/role/listAll',

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        response = await requestReference;
      } catch (error: unknown) {
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      switch (true) {
        case (response?.body?.code === 3000):
          // "Listed roles with sucesses", 3000
          setRoles(response?.body.roles);
          setTableDefinition((previous) => {
            return ({
              ...previous,
              loading: true,
            });
          });

          executeFilter({
            page: (tablePagination?.current)
              ? tablePagination.current - 1
              : 0,
          });
          break;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        case (responseError != null):
        default:
          notification.error({
            message: 'Erro',
            description: 'Desculpe, houve um erro ao buscar os perfis.',
          });
          break;
      }
    };

    firstMount();
    return () => {
      setData([]);
    };
  }, []);

  return (
    <div>
      <div className="service-title">Usuários</div>
      <div className="ListUsers">
        <Row gutter={[24, 24]}>
          <Col span={16}>
            <Form
              scrollToFirstError
              form={formInstance}
              layout="inline"
              onValuesChange={debounce(handleChangedValues, 300)}
              initialValues={{
                nome: undefined,
                tipo: 'COOPERATIVE',
                perfil: undefined,
                status: 'ALL',
                aprovacaoPendente: false,
              }}
            >
              <Row gutter={[8, 24]}>
                <Col>
                  <Form.Item
                    label="Filtros"
                    name="nome"
                    rules={[
                      {
                        pattern: /[a-zA-ZáÁãÃâçàÀÉéêÍíÓóôÔÕõÚú' ]/,
                        message: 'Nome inválido',
                      },
                    ]}
                    getValueFromEvent={
                      (eventt): any => eventt.target.value.replace(/[^A-Za-záàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ\s]/g, '')
                    }
                  >
                    <Input
                      autoComplete="name"
                      name="nome"
                      type="text"
                      placeholder="Nome"
                    />
                  </Form.Item>
                </Col>

                <Col>
                  <Dropdown
                    overlay={(
                      <Menu
                        items={[
                          {
                            className: 'dropMenuWhiteBackground',
                            label: (
                              <Form.Item
                                name="tipo"
                                rules={[
                                  {
                                    required: true,
                                    message: 'Opção inválida',
                                  },
                                ]}
                              >
                                <Radio.Group name="tipo">
                                  {
                                    tipos.map((tipo) => (
                                      <Radio
                                        style={radioStyle}
                                        key={tipo.value}
                                        value={tipo.value}
                                      >
                                        {tipo.name}
                                      </Radio>
                                    ))
                                  }
                                </Radio.Group>
                              </Form.Item>
                            ),
                          },
                        ]}
                      />
                    )}
                    placement="bottomLeft"
                  >
                    <Form.Item
                      shouldUpdate
                    >
                      {
                        (formInstanceParameter: FormInstance): React.ReactNode => {
                          return (
                            <Button
                              className={
                                !formInstanceParameter.isFieldsTouched()
                                  || formInstanceParameter
                                    .getFieldValue('tipo') !== undefined
                                  ? 'ListUsers__filter--active'
                                  : ''
                              }
                            >
                              Tipo
                            </Button>
                          );
                        }
                      }
                    </Form.Item>
                  </Dropdown>
                </Col>

                <Col>
                  <Dropdown
                    overlay={(
                      <Menu
                        items={[
                          {
                            className: 'dropMenuWhiteBackground',

                            label: (
                              <Form.Item
                                shouldUpdate
                              >
                                {
                                  (formInstanceParameter: FormInstance): React.ReactNode => {
                                    return (
                                      <Form.Item
                                        name="perfil"
                                      >
                                        <Radio.Group
                                          name="perfil"
                                        >
                                          {
                                            roles
                                              .concat({ id: undefined, name: 'Todos', type: 'ALL' })
                                              .filter(
                                                (element) => element.type === formInstanceParameter
                                                  .getFieldValue('tipo')
                                                  || element.type === 'ALL',
                                              )
                                              .map((perfil) => (
                                                <Radio
                                                  style={radioStyle}
                                                  key={perfil.name}
                                                  value={perfil.id}
                                                >
                                                  {perfil.name}
                                                </Radio>
                                              ))
                                          }
                                        </Radio.Group>
                                      </Form.Item>
                                    );
                                  }
                                }
                              </Form.Item>
                            ),
                          },
                        ]}
                      />
                    )}
                    placement="bottomLeft"
                  >
                    <Form.Item
                      shouldUpdate
                    >
                      {
                        (formInstanceParameter: FormInstance): React.ReactNode => {
                          return (
                            <Button
                              className={
                                (formInstanceParameter.getFieldValue('perfil') !== undefined
                                  && formInstanceParameter.getFieldValue('perfil') !== 'ALL')
                                  ? 'ListUsers__filter--active'
                                  : ''
                              }
                            >
                              Perfil
                            </Button>
                          );
                        }
                      }
                    </Form.Item>
                  </Dropdown>
                </Col>

                <Col>
                  <Dropdown
                    overlay={(
                      <Menu
                        items={[
                          {
                            className: 'dropMenuWhiteBackground',

                            label: (
                              <Form.Item
                                shouldUpdate
                              >
                                {
                                  (formInstanceParameter: FormInstance): React.ReactNode => {
                                    return (
                                      <Form.Item
                                        name="status"
                                      >
                                        <Radio.Group>
                                          {
                                            statuss
                                              .filter(
                                                (element) => element
                                                  .types
                                                  .includes(formInstanceParameter.getFieldValue('tipo')),
                                              )
                                              .map((stat) => (
                                                <Radio
                                                  style={radioStyle}
                                                  key={stat.code}
                                                  value={stat.code}
                                                >
                                                  {stat.description}
                                                </Radio>
                                              ))
                                          }
                                        </Radio.Group>
                                      </Form.Item>
                                    );
                                  }
                                }
                              </Form.Item>
                            ),
                          },
                        ]}
                      />
                    )}
                    placement="bottomLeft"
                  >

                    <Form.Item
                      shouldUpdate
                    >
                      {
                        (formInstanceParameter: FormInstance): React.ReactNode => {
                          return (
                            <Button
                              className={
                                (formInstanceParameter.getFieldValue('status') !== undefined
                                  && formInstanceParameter.getFieldValue('status') !== 'ALL')
                                  ? 'ListUsers__filter--active'
                                  : ''
                              }
                            >
                              Status
                            </Button>
                          );
                        }
                      }
                    </Form.Item>
                  </Dropdown>
                </Col>

                <Col>
                  <Form.Item
                    noStyle
                    shouldUpdate
                  >
                    {
                      (formInstanceParameter: FormInstance): React.ReactNode => {
                        if (formInstanceParameter.getFieldValue('tipo') === 'COOPERATIVE') {
                          return (
                            <Form.Item
                              label="Matrícula com aprovação pendente"
                              name="aprovacaoPendente"
                              valuePropName="checked"
                            >
                              <Checkbox
                                name="aprovacaoPendente"
                                disabled={
                                  !(formInstanceParameter.getFieldValue('tipo') === 'COOPERATIVE')
                                }
                              />
                            </Form.Item>
                          );
                        }

                        return null;
                      }
                    }
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
          <Col span={4}>
            <Button
              block
              type="primary"
              htmlType="button"
              onClick={exportExcel}
              className="ListUsers__new-user"
              loading={loadingExcel}
            >
              Exportar
            </Button>
          </Col>
          {/* <Col span={4}>
            <Button
              block
              type="primary"
              htmlType="button"
              onClick={() => { navigate('/user/register'); }}
              className="ListUsers__new-user"
            >
              Novo usuário
            </Button>
          </Col> */}
        </Row>

        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Table
              loading={tableDefinition.loading}
              pagination={{
                pageSize: tablePagination.pageSize || 15,
                current: tablePagination.current || 1,
                defaultCurrent: tablePagination.defaultCurrent || 1,
                showQuickJumper: tablePagination.showQuickJumper || true,
                total: tablePagination.total || 0,
              }}
              className="ListUsers__table"
              rowKey={(record): string => record.id as unknown as string}
              dataSource={data}
              columns={columns}
              onChange={handleTableChange}
              scroll={{
                x: '100vw',
              }}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};

ListUsers.propTypes = {};

ListUsers.defaultProps = {};

export default ListUsers;
